import { motion, useInView } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { MapPin } from "lucide-react";

const storyText = "Pura Madre nació en Ciudad de México con una misión clara: hacer salsas que realmente valgan la pena. Sin conservadores, sin químicos. Solo ingredientes del campo mexicano desarrollados por un ingeniero biotecnólogo con obsesión por el sabor y la calidad. Cada frasco es una declaración: la salsa mexicana merece ser gourmet.";

function TypewriterText({ text, speed = 25 }) {
  const [displayed, setDisplayed] = useState("");
  const [done, setDone] = useState(false);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.2 });

  useEffect(() => {
    if (!inView) return;
    let i = 0;
    const interval = setInterval(() => {
      if (i < text.length) {
        setDisplayed(text.slice(0, i + 1));
        i++;
      } else {
        setDone(true);
        clearInterval(interval);
      }
    }, speed);
    return () => clearInterval(interval);
  }, [inView, text, speed]);

  return (
    <span ref={ref} className="text-base md:text-lg text-[#A3A3A3] leading-relaxed">
      {displayed}
      {!done && <span className="typewriter-cursor" />}
    </span>
  );
}

const stats = [
  { value: "100%", label: "NATURAL" },
  { value: "0", label: "CONSERVADORES" },
  { value: "2", label: "FUNDADORES" },
];

export default function NuestraHistoria() {
  return (
    <section id="story" data-testid="nuestra-historia-section" className="relative py-24 md:py-32 overflow-hidden" style={{ backgroundColor: "#1A1A1A" }}>
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: "url('https://grainy-gradients.vercel.app/noise.svg')" }} />
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.5 }} className="relative">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img src="https://images.unsplash.com/photo-1764154727059-8320ad2052f4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHw0fHxtZXhpY2FuJTIwZm9vZCUyMHRhY29zJTIwZ291cm1ldCUyMGRhcmslMjBtb29keXxlbnwwfHx8fDE3NzE5NjQ3ODN8MA&ixlib=rb-4.1.0&q=85" alt="Ingredientes Pura Madre" className="w-full h-full object-cover" loading="lazy" data-testid="historia-image" />
              <div className="absolute inset-0 bg-[#1A1A1A]/20" />
            </div>
            <div className="absolute -bottom-4 -right-4 w-32 h-32 border-2 border-[#D4AF37]/30" />
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.5 }}>
            <div className="flex items-center gap-3 mb-6">
              <MapPin size={18} className="text-[#E07A5F]" />
              <span className="text-sm uppercase tracking-[0.3em] text-[#D4AF37]" data-testid="hecho-en-mexico">Hecho en México</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#F5F5F0] tracking-tight mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>
              Dos locos que creyeron<br /><span className="text-[#E07A5F]">en el sabor auténtico</span>
            </h2>
            <div className="mb-10 min-h-[140px]" data-testid="typewriter-text">
              <TypewriterText text={storyText} speed={20} />
            </div>
            <div className="flex flex-wrap gap-10">
              {stats.map((stat) => (
                <motion.div key={stat.label} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.5 }} transition={{ duration: 0.3 }} className="text-center" data-testid={`stat-${stat.label.toLowerCase()}`}>
                  <div className="text-3xl font-bold text-[#D4AF37]" style={{ fontFamily: "'Playfair Display', serif" }}>{stat.value}</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3] mt-1">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
